import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { StorageSection } from "@/components/admin/StorageSection";
import { AuditSection } from "@/components/admin/AuditSection";
import { PresetGovernance } from "@/components/admin/PresetGovernance";
import { DigestPrefs } from "@/components/admin/DigestPrefs";
import { MostUsedWidget } from "@/components/admin/MostUsedWidget";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { KeyRound, Trash2, Pencil } from "lucide-react";
import { CoverPreview } from "@/components/CoverPreview";
import { AdminProjects } from "@/components/admin/AdminProjects";
import { AllUsersProjects } from "@/components/admin/AllUsersProjects";
import { OwnerActivityLog } from "@/components/admin/OwnerActivityLog";
import { ImageryStreams } from "@/components/admin/ImageryStreams";

const ROLES = [
  ["super_admin", "Super Admin"], ["admin", "Admin"], ["user", "User"],
  ["company_admin", "Company Admin"],
  ["remote_sensing_specialist", "Remote-Sensing Specialist"], ["geologist", "Geologist"],
  ["gis_analyst", "GIS Analyst"], ["mining_engineer", "Mining Engineer"],
  ["reviewer", "Reviewer"], ["client_viewer", "Client Viewer"],
];

const INACTIVE_DAYS = 30;
const isInactive = (u) => (!u.last_login
  ? true
  : (Date.now() - new Date(u.last_login).getTime()) > INACTIVE_DAYS * 864e5);

const MODULE_DEFS = [
  ["remote-sensing", "Remote Sensing"],
  ["maps", "Geomapping"],
  ["geophysics", "Geophysics"],
];
export default function Admin() {
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const { refreshBranding } = useBranding();
  const { user: me } = useAuth();
  const [branding, setBrandingState] = useState({ logo: null, company_name: "", cover_template: "navy" });
  const [savingBrand, setSavingBrand] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [planet, setPlanet] = useState({ configured: false, masked: null });
  const [planetKey, setPlanetKey] = useState("");
  const [planetBusy, setPlanetBusy] = useState(false);
  useEffect(() => { api.get("/admin/planet-key").then((r) => setPlanet(r.data)).catch(() => {}); }, []);
  const savePlanetKey = async () => {
    if (planetKey.trim().length < 10) return toast.error("Enter a valid Planet API key");
    setPlanetBusy(true);
    try {
      await api.post("/admin/planet-key", { api_key: planetKey.trim() });
      toast.success("Planet key validated & saved"); setPlanetKey("");
      const { data } = await api.get("/admin/planet-key"); setPlanet(data);
    } catch (e) { toast.error(apiError(e)); } finally { setPlanetBusy(false); }
  };
  const removePlanetKey = async () => {
    try { await api.delete("/admin/planet-key"); setPlanet({ configured: false }); toast.success("Planet key removed"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [modTarget, setModTarget] = useState(null);
  const [modForm, setModForm] = useState([]);
  const openModules = (u) => {
    setModTarget(u);
    setModForm(MODULE_DEFS.map(([k]) => {
      const ex = (u.modules || []).find((m) => m.module === k);
      return { module: k, enabled: !!ex, start: ex?.start || "", expiry: ex?.expiry || "" };
    }));
  };
  const saveModules = async () => {
    try {
      const modules = modForm.filter((m) => m.enabled).map((m) => ({ module: m.module, start: m.start || null, expiry: m.expiry || null }));
      await api.put(`/admin/users/${modTarget.id}/modules`, { modules });
      toast.success("Module access updated"); setModTarget(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const [selIds, setSelIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const toggleSel = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: selIds });
      toast.success(`Deleted ${data.deleted} user(s)${data.skipped ? ` · ${data.skipped} protected/invalid skipped` : ""}`);
      setSelIds([]); setBulkOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/admin/branding").then((r) => setBrandingState(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const createUser = async () => {
    try { await api.post("/admin/users", form); toast.success("User created"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approveUser = async (u) => {
    try { await api.post(`/admin/users/${u.id}/approve`); toast.success(`Approved "${u.name}" - they can now sign in`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectUser = async (u) => {
    try { await api.post(`/admin/users/${u.id}/reject`); toast.success(`Rejected and removed "${u.name}"`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPassword = async () => {
    if (pw1.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setPwBusy(true);
    try {
      await api.post(`/admin/users/${pwTarget.id}/reset-password`, { new_password: pw1 });
      toast.success(`Password reset for ${pwTarget.name || pwTarget.email}`);
      setPwTarget(null); setPw1(""); setPw2("");
    } catch (e) { toast.error(apiError(e)); } finally { setPwBusy(false); }
  };
  const deleteUser = async () => {
    try {
      await api.delete(`/admin/users/${delTarget.id}`);
      toast.success("User deleted"); setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveName = async () => {
    if (!editName.trim()) return toast.error("Name is required");
    try {
      await api.put(`/admin/users/${editTarget.id}`, { name: editName.trim() });
      toast.success("User updated"); setEditTarget(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setSavingBrand(true);
      try {
        const { data } = await api.put("/admin/branding", { logo: reader.result });
        setBrandingState(data); refreshBranding(); toast.success("Logo updated - now live across the app & reports");
      } catch (err) { toast.error(apiError(err)); }
      finally { setSavingBrand(false); }
    };
    reader.readAsDataURL(f);
  };
  const deleteLogo = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.delete("/admin/branding/logo");
      setBrandingState(data); refreshBranding(); toast.success("Logo removed - using SYMIN wordmark");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingBrand(false); }
  };
  const saveCompany = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.put("/admin/branding", { company_name: branding.company_name });
      setBrandingState(data); refreshBranding(); toast.success("Company name saved");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingBrand(false); }
  };
  const setTemplate = async (t) => {
    setSavingBrand(true);
    try {
      const { data } = await api.put("/admin/branding", { cover_template: t });
      setBrandingState(data); refreshBranding(); toast.success("Default cover template updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingBrand(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in">
      <div><p className="overline">Master Oversight</p><h1 className="font-display text-3xl font-bold tracking-tight">Master Oversight Dashboard</h1></div>
      <MostUsedWidget />
      <Tabs defaultValue="all-projects">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
        <TabsList className="w-max">
          <TabsTrigger value="all-projects" data-testid="admin-tab-all-projects">All Projects</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity Log</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users & Roles</TabsTrigger>
          <TabsTrigger value="providers" data-testid="admin-tab-providers">Satellite Providers</TabsTrigger>
          <TabsTrigger value="projects" data-testid="admin-tab-projects">Projects</TabsTrigger>
          <TabsTrigger value="branding" data-testid="admin-tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="presets" data-testid="admin-tab-presets">Band Presets</TabsTrigger>
          <TabsTrigger value="system" data-testid="admin-tab-system">System</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="all-projects" className="mt-5">
          <AllUsersProjects />
        </TabsContent>

        <TabsContent value="activity" className="mt-5">
          <OwnerActivityLog />
        </TabsContent>

        <TabsContent value="users" className="mt-5">
          <div className="flex justify-between items-center mb-3 gap-3">
            {selIds.length > 0 ? (
              <div className="flex items-center gap-2 text-sm" data-testid="users-bulk-bar">
                <span className="text-muted-foreground">{selIds.length} selected</span>
                <Button variant="destructive" size="sm" onClick={() => setBulkOpen(true)} data-testid="users-bulk-delete">Delete selected</Button>
                <Button variant="ghost" size="sm" onClick={() => setSelIds([])} data-testid="users-bulk-clear">Clear</Button>
              </div>
            ) : <div />}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="admin-new-user">Add User</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Create User</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="au-name" /></div>
                  <div><Label>Username / Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="au-email" placeholder="e.g. jsmith or j.smith@company.com" /></div>
                  <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="au-password" /></div>
                  <div><Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-1" data-testid="au-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={createUser} data-testid="au-create">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="shadow-none overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead><tr className="border-b border-border text-left">
                <th className="px-4 py-3 w-8"><input type="checkbox" data-testid="users-select-all"
                  checked={selIds.length > 0 && selIds.length === users.filter((x) => x.email !== me?.email).length}
                  onChange={(e) => setSelIds(e.target.checked ? users.filter((x) => x.email !== me?.email).map((x) => x.id) : [])} className="h-4 w-4 accent-primary" /></th>
                {["Name", "Email", "Role", "Permissions", "Last login", "Actions"].map((h) => <th key={h} className="px-5 py-3 overline">{h}</th>)}
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border/60">
                    <td className="px-4 py-3"><input type="checkbox" data-testid={`user-check-${u.id}`} disabled={u.email === me?.email}
                      checked={selIds.includes(u.id)} onChange={() => toggleSel(u.id)} className="h-4 w-4 accent-primary disabled:opacity-30" /></td>
                    <td className="px-5 py-3 font-medium">{u.name}
                      {me?.email === u.email && <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>}
                      {u.approved === false && <Badge className="ml-2 text-[10px] bg-red-500 text-black hover:bg-red-500" data-testid={`pending-badge-${u.id}`}>Pending approval</Badge>}
                      {u.approved !== false && isInactive(u) && <Badge variant="outline" className="ml-2 text-[10px] border-red-500/50 text-red-600" data-testid={`inactive-badge-${u.id}`} title="No sign-in activity for 30+ days">Inactive 30d+</Badge>}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-5 py-3">
                      <Select value={u.role} onValueChange={(v) => setRole(u.id, v)} disabled={me?.email === u.email}>
                        <SelectTrigger className="h-8 w-52" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3"><Badge variant="secondary">{(u.permissions || []).length} perms</Badge></td>
                    <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap" data-testid={`last-login-${u.id}`}>
                      {u.last_login ? new Date(u.last_login).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : <span className="italic opacity-70">Never</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        {u.approved === false && me?.role === "super_admin" && (
                          <>
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-user-${u.id}`}
                              onClick={() => approveUser(u)}>
                              Approve
                            </Button>
                            <Button variant="outline" size="sm" className="h-8 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive" data-testid={`reject-user-${u.id}`}
                              onClick={() => rejectUser(u)}>
                              Reject
                            </Button>
                          </>
                        )}
                        {me?.role === "super_admin" && u.role !== "super_admin" && u.role !== "client_viewer" && (
                          <Button variant="outline" size="sm" className="h-8" data-testid={`modules-user-${u.id}`}
                            onClick={() => openModules(u)}>
                            Modules{(u.active_modules || []).length ? ` · ${(u.active_modules || []).length}` : ""}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="h-8 px-2" title="Edit user" data-testid={`edit-user-${u.id}`}
                          onClick={() => { setEditTarget(u); setEditName(u.name || ""); }}>
                          <Pencil size={13} />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8" data-testid={`reset-pw-${u.id}`}
                          onClick={() => { setPwTarget(u); setPw1(""); setPw2(""); }}>
                          <KeyRound size={13} className="mr-1.5" /> Reset password
                        </Button>
                        {me?.email !== u.email && (
                          <Button variant="ghost" size="sm" className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid={`delete-user-${u.id}`}
                            onClick={() => setDelTarget(u)}>
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>

          <Dialog open={!!modTarget} onOpenChange={(o) => !o && setModTarget(null)}>
            <DialogContent data-testid="modules-dialog">
              <DialogHeader>
                <DialogTitle>Module access · {modTarget?.name || modTarget?.email}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Assign the modules this user can open. Leave dates blank for immediate / no expiry.</p>
                {modForm.map((m, i) => (
                  <div key={m.module} className="rounded-lg border border-border p-3">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" data-testid={`mod-toggle-${m.module}`} checked={m.enabled}
                        onChange={(e) => setModForm((f) => f.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))}
                        className="h-4 w-4 accent-primary" />
                      <span className="font-semibold text-sm">{MODULE_DEFS[i][1]}</span>
                    </label>
                    {m.enabled && (
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <Label className="text-xs">Start date</Label>
                          <Input type="date" value={m.start || ""} data-testid={`mod-start-${m.module}`}
                            onChange={(e) => setModForm((f) => f.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">Expiry date</Label>
                          <Input type="date" value={m.expiry || ""} data-testid={`mod-expiry-${m.module}`}
                            onChange={(e) => setModForm((f) => f.map((x, j) => (j === i ? { ...x, expiry: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModTarget(null)}>Cancel</Button>
                <Button onClick={saveModules} data-testid="modules-save">Save access</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {me?.role === "super_admin" && <DigestPrefs />}
          {me?.role === "super_admin" && <StorageSection />}
          {me?.role === "super_admin" && <AuditSection />}
        </TabsContent>

        <TabsContent value="providers" className="mt-5">
          <div className="grid md:grid-cols-2 gap-4">
            {providers.map((p) => (
              <Card key={p.key} className="p-5 shadow-none">
                <div className="flex justify-between items-start">
                  <h3 className="font-display font-semibold">{p.name}</h3>
                  <Badge className="bg-priority-low text-white">{p.auth}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-muted-foreground font-mono">
                  <div>Type: {p.type}</div><div>Res: {p.resolution}</div>
                  <div>Collection: {p.collection}</div><div>Endpoint: {p.endpoint}</div>
                </div>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 font-mono">
            Premium providers (Maxar, Planet, Airbus, BlackSky) - Coming in Phase 4. API credentials are stored encrypted server-side.
          </p>
          <ImageryStreams />
        </TabsContent>

        <TabsContent value="projects" className="mt-5">
          <AdminProjects />
        </TabsContent>

        <TabsContent value="branding" className="mt-5">
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4">
            <Card className="p-6 shadow-none space-y-4" data-testid="branding-manager">
              <div>
                <h3 className="font-display font-semibold">Branding Manager</h3>
                <p className="text-xs text-muted-foreground mt-1">Upload your logo to brand every report cover, header and the dashboard. Remove it any time to fall back to the SYMIN wordmark.</p>
              </div>
              <div className="rounded-lg border border-border bg-slate-50 dark:bg-slate-900 grid place-items-center p-6 min-h-[120px]" data-testid="branding-preview">
                {branding.logo
                  ? <img src={branding.logo} alt="Current logo" className="max-h-24 max-w-[240px] object-contain" />
                  : <div className="text-center"><div className="font-display font-extrabold text-2xl tracking-tight text-slate-900 dark:text-white">SYMIN</div></div>}
              </div>
              <div className="flex gap-2 items-center">
                <label className="inline-flex">
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoFile} data-testid="branding-upload-input" />
                  <span className={`inline-flex items-center h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium cursor-pointer ${savingBrand ? "opacity-50 pointer-events-none" : ""}`} data-testid="branding-upload-btn">{savingBrand ? "Saving…" : "Upload logo"}</span>
                </label>
                {branding.logo && <Button variant="outline" onClick={deleteLogo} disabled={savingBrand} data-testid="branding-delete-btn">Remove logo</Button>}
              </div>
              <p className="text-[11px] text-muted-foreground">PNG / JPG / WebP, transparent background recommended, up to 3 MB.</p>
            </Card>
            <Card className="p-6 shadow-none space-y-4">
              <div>
                <h3 className="font-display font-semibold">Company &amp; Cover Style</h3>
                <p className="text-xs text-muted-foreground mt-1">The company name is the &quot;Prepared by&quot; line on covers. The cover style is the default look for every new report (projects can override it).</p>
              </div>
              <div><Label>Company / consultancy name</Label>
                <Input className="mt-1" value={branding.company_name || ""} onChange={(e) => setBrandingState({ ...branding, company_name: e.target.value })} placeholder="SYMIN" data-testid="branding-company-input" /></div>
              <Button onClick={saveCompany} disabled={savingBrand} data-testid="branding-save-company">Save name</Button>
              <div>
                <Label className="mb-1.5 block">Default cover template</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="branding-template-picker">
                  {[["navy", "Navy Hero"], ["light", "Light Editorial"], ["minimal", "Minimal"]].map(([v, l]) => (
                    <button key={v} data-testid={`branding-template-${v}`} onClick={() => setTemplate(v)} disabled={savingBrand}
                      className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${(branding.cover_template || "navy") === v ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:border-primary/50"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </Card>
            <Card className="p-6 shadow-none space-y-3" data-testid="branding-live-preview">
              <div>
                <h3 className="font-display font-semibold">Live Cover Preview</h3>
                <p className="text-xs text-muted-foreground mt-1">A miniature of your master report cover with the current logo, name and template.</p>
              </div>
              <CoverPreview logo={branding.logo} companyName={branding.company_name} template={branding.cover_template || "navy"} />
            </Card>
          </div>
          <Card className="p-5 shadow-none mt-4" data-testid="planet-key-card">
            <h3 className="font-display font-bold">Premium Imagery - Planet API Key</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Add your Planet Labs API key (Account → My Settings) to unlock 3 m PlanetScope previews and downloads on every licence area. The key stays server-side and is never shown to users.
            </p>
            {planet.configured ? (
              <div className="mt-3 flex items-center gap-3 text-sm">
                <Badge className="bg-emerald-600 text-white" data-testid="planet-key-status">Active · {planet.masked}</Badge>
                <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={removePlanetKey} data-testid="planet-key-remove">Remove</Button>
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-2 max-w-md">
                <Input type="password" placeholder="PLAK…" value={planetKey} onChange={(e) => setPlanetKey(e.target.value)} data-testid="planet-key-input" />
                <Button onClick={savePlanetKey} disabled={planetBusy} data-testid="planet-key-save">{planetBusy ? "Validating…" : "Validate & Save"}</Button>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="system" className="mt-5">
          <div className="grid md:grid-cols-3 gap-4">
            {["Companies", "Report Templates", "Map Templates", "AI Models", "Billing", "Audit Logs", "Backups", "Notifications", "Storage"].map((m) => (
              <Card key={m} className="p-5 shadow-none">
                <h3 className="font-display font-semibold">{m}</h3>
                <p className="text-xs text-muted-foreground mt-1">Coming in Phase 2</p>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="presets" className="mt-5">
          <PresetGovernance />
        </TabsContent>
      </Tabs>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reset password - {pwTarget?.name || pwTarget?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Set a new password for <b className="font-mono">{pwTarget?.email}</b>. They can change it themselves afterwards.</p>
            <div><Label>New password</Label>
              <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} className="mt-1" placeholder="At least 8 characters" data-testid="admin-pw-new" /></div>
            <div><Label>Confirm new password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" data-testid="admin-pw-confirm" /></div>
          </div>
          <DialogFooter>
            <Button onClick={resetPassword} disabled={pwBusy} className="w-full" data-testid="admin-pw-submit">{pwBusy ? "Saving…" : "Reset password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit user - {editTarget?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Display name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1" data-testid="admin-edit-name" /></div>
            <p className="text-xs text-muted-foreground">Role is changed directly in the table; password via “Reset password”. Usernames are permanent identifiers.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveName} className="w-full" data-testid="admin-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirm */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user “{delTarget?.name || delTarget?.email}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the account <b className="font-mono">{delTarget?.email}</b>. Their projects remain intact. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-delete-confirm">Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete users confirm */}
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="users-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selIds.length} user{selIds.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Selected accounts are permanently removed (your own account and server-configured admins are automatically skipped). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="users-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="users-bulk-confirm">Delete selected</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
